import { Tooltip, Progress } from "antd";
import {
  TrophyFilled,
  SearchOutlined,
  SafetyCertificateOutlined,
  FieldTimeOutlined,
  DashboardOutlined,
  DollarCircleOutlined,
  AccountBookOutlined,
  StarFilled,
  StarOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { METRICS, metricDelta, runMetric, type CompareRun } from "../lib/align";

export function getDeltaExplanation(
  key: string,
  diff: number,
  isBetter: boolean | null,
): string {
  if (isBetter === null) return "No difference.";
  const absDiff = Math.abs(diff);

  switch (key) {
    case "accuracyRate":
    case "successRate":
      if (absDiff > 0.15)
        return isBetter
          ? "Huge improvement in quality"
          : "Huge drop in quality";
      if (absDiff > 0.05)
        return isBetter ? "Noticeable improvement" : "Noticeable drop";
      return "Slight difference";
    case "averageQualityScore":
      if (absDiff > 1.5)
        return isBetter
          ? "Significantly more trusted by judges"
          : "Significantly less trusted by judges";
      if (absDiff > 0.5)
        return isBetter ? "Noticeably better" : "Noticeably worse";
      return "Slightly different";
    case "averageLatencyMs":
    case "p95LatencyMs":
      if (absDiff > 1500)
        return isBetter ? "Model A is much slower." : "Model B is much slower.";
      if (absDiff > 500)
        return isBetter ? "Noticeably faster" : "Noticeably slower";
      return "Slight latency diff";
    case "totalTokens":
      if (absDiff > 5000)
        return isBetter
          ? "Fewer tokens used by Model B."
          : "More tokens used by Model B.";
      return "Token usage differs slightly";
    case "totalCost":
      if (absDiff > 0.0001)
        return isBetter
          ? "Significant cost savings."
          : "Significantly more expensive.";
      return "Minor cost difference.";
    default:
      return isBetter ? "Better." : "Worse.";
  }
}

const StarRating = ({ value, color }: { value: number; color: string }) => {
  const stars = [];
  for (let i = 1; i <= 10; i++) {
    if (i <= Math.round(value)) {
      stars.push(<StarFilled key={i} style={{ color }} />);
    } else {
      stars.push(<StarOutlined key={i} className="text-gray-200" />);
    }
  }
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-0.5 text-[10px]">{stars}</div>
      <div className="flex gap-[7px] text-[8px] text-gray-400 mt-1 pl-1">
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
        <span>6</span>
        <span>7</span>
        <span>8</span>
        <span>9</span>
        <span className="-ml-0.5">10</span>
      </div>
    </div>
  );
};

const GaugeVisual = ({ r1, r2, v1, v2, def }: any) => {
  const d = metricDelta(def, v2, v1);
  return (
    <div className="flex justify-between items-center relative mt-4">
      <div className="absolute top-[40%] left-0 w-full h-4 -translate-y-1/2 flex items-center px-12 -z-10">
        <div
          className="w-full h-2 rounded-full opacity-30"
          style={{
            background: `linear-gradient(to right, ${r1.color}, ${r2.color})`,
          }}
        />
      </div>

      <div className="flex flex-col items-center z-10 bg-white rounded-full pt-1 px-1">
        <Progress
          type="dashboard"
          percent={v1 ? v1 * 100 : 0}
          gapDegree={180}
          strokeColor={r1.color}
          size={80}
          format={() => ""}
          strokeWidth={14}
        />
        <div className="text-sm font-bold -mt-8">{def.format(v1)}</div>
      </div>

      <div className="flex flex-col items-center z-20 absolute left-1/2 -translate-x-1/2">
        {d && d.better !== null && (
          <div className="flex flex-col items-center">
            <div
              className="bg-white/95 backdrop-blur px-3 py-0.5 rounded-full shadow-sm border border-gray-100 flex items-center gap-1 font-bold text-sm"
              style={{ color: d.better ? "#10b981" : "#f43f5e" }}
            >
              {d.diff > 0 ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
            </div>
            <div className="mt-1 bg-gray-50 text-gray-600 text-[10px] px-2 py-1 rounded-md border border-gray-200 whitespace-nowrap shadow-sm absolute top-full mt-2">
              {getDeltaExplanation(def.key, d.diff, d.better)}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center z-10 bg-white rounded-full pt-1 px-1">
        <Progress
          type="dashboard"
          percent={v2 ? v2 * 100 : 0}
          gapDegree={180}
          strokeColor={r2.color}
          size={80}
          format={() => ""}
          strokeWidth={14}
        />
        <div className="text-sm font-bold -mt-8">{def.format(v2)}</div>
      </div>
    </div>
  );
};

const StarsVisual = ({ r1, r2, v1, v2, def }: any) => {
  const d = metricDelta(def, v2, v1);
  return (
    <div className="flex justify-between items-center relative mt-6 mb-2">
      <div className="absolute top-[30%] left-0 w-full h-px -translate-y-1/2 flex items-center px-12 -z-10">
        <div
          className="w-full h-0.5 rounded-full opacity-30 blur-[1px]"
          style={{
            background: `linear-gradient(to right, ${r1.color}, ${r2.color})`,
          }}
        />
      </div>
      <div className="z-10 bg-white px-2">
        <StarRating value={v1 || 0} color={r1.color} />
      </div>
      <div className="flex flex-col items-center z-20 absolute left-1/2 -translate-x-1/2 mt-8">
        {d && d.better !== null && (
          <div className="flex flex-col items-center">
            <div
              className="bg-white/95 backdrop-blur px-2 py-0.5 rounded-full shadow-sm border border-gray-100 flex items-center gap-1 font-bold text-sm"
              style={{ color: d.better ? "#10b981" : "#f43f5e" }}
            >
              {d.diff > 0 ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
            </div>
            <div className="mt-1 bg-gray-50 text-gray-600 text-[10px] px-2 py-1 rounded-md border border-gray-200 whitespace-nowrap shadow-sm relative z-30">
              {getDeltaExplanation(def.key, d.diff, d.better)}
            </div>
          </div>
        )}
      </div>
      <div className="z-10 bg-white px-2">
        <StarRating value={v2 || 0} color={r2.color} />
      </div>
    </div>
  );
};

const CircleVisual = ({ r1, r2, v1, v2, def }: any) => {
  const d = metricDelta(def, v2, v1);
  return (
    <div className="flex justify-between items-center relative mt-4 px-2">
      <div className="absolute top-1/2 left-0 w-full h-4 -translate-y-1/2 flex items-center px-12 -z-10">
        <div
          className="w-full h-2 rounded-full opacity-30"
          style={{
            background: `linear-gradient(to right, ${r1.color}, ${r2.color})`,
          }}
        />
      </div>
      <div className="flex flex-col items-center z-10 bg-white rounded-full p-1">
        <Progress
          type="circle"
          percent={v1 ? v1 * 100 : 0}
          strokeColor={r1.color}
          size={64}
          format={() => ""}
          strokeWidth={16}
        />
        <div className="text-[12px] font-bold absolute mt-5">
          {def.format(v1)}
        </div>
      </div>
      <div className="flex flex-col items-center z-20 absolute left-1/2 -translate-x-1/2">
        {d && d.better !== null && (
          <div className="flex flex-col items-center">
            <div
              className="bg-white/95 backdrop-blur px-3 py-0.5 rounded-full shadow-sm border border-gray-100 flex items-center gap-1 font-bold text-sm"
              style={{ color: d.better ? "#10b981" : "#f43f5e" }}
            >
              {d.diff > 0 ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
            </div>
            <div className="mt-1 bg-gray-50 text-gray-600 text-[10px] px-2 py-1 rounded-md border border-gray-200 whitespace-nowrap shadow-sm absolute top-full mt-2">
              {getDeltaExplanation(def.key, d.diff, d.better)}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col items-center z-10 bg-white rounded-full p-1">
        <Progress
          type="circle"
          percent={v2 ? v2 * 100 : 0}
          strokeColor={r2.color}
          size={64}
          format={() => ""}
          strokeWidth={16}
        />
        <div className="text-[12px] font-bold absolute mt-5">
          {def.format(v2)}
        </div>
      </div>
    </div>
  );
};

const MetricRow = ({ title, r1, r2, v1, v2, def, tooltip, children }: any) => {
  return (
    <div className="flex flex-col mb-8 last:mb-0">
      <div className="flex justify-between items-center w-full mb-2">
        <Tooltip title={tooltip}>
          <span className="font-bold text-base text-gray-900 cursor-help flex items-center gap-1">
            {title}{" "}
            <span className="text-gray-400 font-normal border border-gray-200 rounded-full w-[14px] h-[14px] flex items-center justify-center text-[9px]">
              ?
            </span>
          </span>
        </Tooltip>
        <div className="flex items-center gap-3 font-bold text-sm tracking-wide">
          <span style={{ color: r1.color }}>A: {def.format(v1)}</span>
          <span style={{ color: r2.color }}>B: {def.format(v2)}</span>
        </div>
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
};

const BarVisual = ({ r1, r2, v1, v2, def }: any) => {
  const max = Math.max(v1 || 0, v2 || 0) * 1.2 || 1;
  const h1 = ((v1 || 0) / max) * 100;
  const h2 = ((v2 || 0) / max) * 100;
  const d = metricDelta(def, v2, v1);

  return (
    <div className="flex items-end gap-10 h-32 relative mt-4 mb-4">
      {/* Grouped Bars */}
      <div className="flex items-end gap-6 h-full relative z-10 px-4 border-b-[3px] border-gray-200/60 ml-4 pb-0">
        {/* Bar A */}
        <div className="relative group w-11 h-full flex items-end">
          <div
            className="w-full rounded-t-sm shadow-sm relative"
            style={{ height: `${Math.max(4, h1)}%`, backgroundColor: r1.color }}
          >
            <div className="absolute inset-0 overflow-hidden rounded-t-sm">
              <div className="absolute inset-x-0 top-0 h-1.5 bg-white/40" />
              <div className="absolute inset-y-0 right-0 w-1.5 bg-black/15" />
            </div>
            <span className="text-[11px] font-bold text-gray-700 absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap">
              {def.format(v1)}
            </span>
          </div>
          <div
            className="w-7 h-7 rounded-lg text-[11px] font-bold text-white flex items-center justify-center shadow-sm absolute -bottom-3.5 left-1/2 -translate-x-1/2 border-2 border-white"
            style={{ backgroundColor: r1.color, zIndex: 20 }}
          >
            A
          </div>
        </div>

        {/* Bar B */}
        <div className="relative group w-11 h-full flex items-end">
          <div
            className="w-full rounded-t-sm shadow-sm relative"
            style={{ height: `${Math.max(4, h2)}%`, backgroundColor: r2.color }}
          >
            <div className="absolute inset-0 overflow-hidden rounded-t-sm">
              <div className="absolute inset-x-0 top-0 h-1.5 bg-white/40" />
              <div className="absolute inset-y-0 right-0 w-1.5 bg-black/15" />
            </div>
            <span className="text-[11px] font-bold text-gray-700 absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap">
              {def.format(v2)}
            </span>
          </div>
          <div
            className="w-7 h-7 rounded-lg text-[11px] font-bold text-white flex items-center justify-center shadow-sm absolute -bottom-3.5 left-1/2 -translate-x-1/2 border-2 border-white"
            style={{ backgroundColor: r2.color, zIndex: 20 }}
          >
            B
          </div>
        </div>
      </div>

      {/* Delta Section on the Right */}
      <div className="flex flex-col items-start z-20 mb-8 relative">
        {/* Connecting gradient swoosh */}
        <div
          className="absolute right-[110%] top-1/2 w-12 h-10 -translate-y-1/2 -z-10 opacity-30 pointer-events-none"
          style={{
            background: `linear-gradient(to right, ${r2.color}, transparent)`,
            clipPath: "polygon(0 40%, 100% 0, 100% 100%, 0 60%)",
          }}
        />

        {d && d.better !== null && (
          <div className="flex flex-col items-start">
            <div
              className="font-bold text-[16px] flex items-center gap-1 drop-shadow-sm"
              style={{ color: d.better ? "#10b981" : "#f43f5e" }}
            >
              {d.diff > 0 ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
            </div>
            <div className="mt-1 bg-[#f8f9fa] text-gray-700 text-[11px] px-3 py-2 rounded-lg border border-gray-200 whitespace-nowrap shadow-sm relative font-medium">
              <div className="absolute -top-1.5 left-4 w-3 h-3 bg-[#f8f9fa] border-t border-l border-gray-200 rotate-45" />
              <span className="relative z-10">
                {getDeltaExplanation(def.key, d.diff, d.better)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const COIN_STACK_MAX = 12;

/** Coins in a stack, sized proportionally to the value (relative to the larger of the two runs) so the
 * taller stack always reflects the larger number. Returns 0 for null/zero values so unknown or
 * truly-zero totals render an empty stack; positive values always render at least one coin. */
const coinCount = (value: number | null, max: number): number => {
  if (!value || value <= 0) return 0;
  return Math.max(1, Math.round((value / max) * COIN_STACK_MAX));
};

const CoinsVisual = ({ r1, r2, v1, v2, def }: any) => {
  const d = metricDelta(def, v2, v1);
  const max = Math.max(v1 || 0, v2 || 0) || 1;
  const count1 = coinCount(v1, max);
  const count2 = coinCount(v2, max);
  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-end px-4 mt-6 h-32 relative">
        <div className="absolute top-[60%] left-0 w-full h-px -translate-y-1/2 flex items-center px-8 -z-10">
          <div
            className="w-full h-8 rounded-full opacity-20 blur-md"
            style={{
              background: `linear-gradient(to right, ${r1.color}, ${r2.color})`,
            }}
          />
        </div>

        <div className="flex flex-col items-center gap-2 z-10 w-1/2">
          <div className="relative w-16 flex justify-center h-20">
            {Array.from({ length: count1 }).map((_, i) => (
              <div
                key={i}
                className="w-14 h-6 rounded-[50%] absolute shadow-[0_2px_4px_rgba(0,0,0,0.15)] border border-black/10"
                style={{ backgroundColor: r1.color, bottom: i * 5, zIndex: i }}
              >
                <div className="absolute inset-0 rounded-[50%] bg-gradient-to-tr from-black/20 to-transparent" />
                <div className="absolute top-1 left-2 w-10 h-4 rounded-[50%] border-t border-white/50" />
              </div>
            ))}
          </div>
          <div className="font-bold text-gray-800 text-sm mt-3">
            {def.format(v1)}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 z-10 w-1/2">
          <div className="relative w-16 flex justify-center h-20">
            {Array.from({ length: count2 }).map((_, i) => (
              <div
                key={i}
                className="w-14 h-6 rounded-[50%] absolute shadow-[0_2px_4px_rgba(0,0,0,0.15)] border border-black/10"
                style={{ backgroundColor: r2.color, bottom: i * 5, zIndex: i }}
              >
                <div className="absolute inset-0 rounded-[50%] bg-gradient-to-tr from-black/20 to-transparent" />
                <div className="absolute top-1 left-2 w-10 h-4 rounded-[50%] border-t border-white/50" />
              </div>
            ))}
          </div>
          <div className="font-bold text-gray-800 text-sm mt-3">
            {def.format(v2)}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center mt-6">
        {d && d.better !== null && (
          <div className="flex flex-col items-center">
            <div
              className="flex items-center gap-1 font-bold text-sm"
              style={{ color: d.better ? "#10b981" : "#f43f5e" }}
            >
              {d.diff > 0 ? "▲" : "▼"} {def.format(Math.abs(d.diff))}
            </div>
            <div className="mt-2 bg-gray-50 text-gray-600 text-[10px] px-3 py-1.5 rounded-md border border-gray-200 shadow-sm text-center">
              {getDeltaExplanation(def.key, d.diff, d.better)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CostRow = ({ title, tooltip, r1, r2, v1, v2, def }: any) => {
  return (
    <div className="flex flex-col items-center border-r border-gray-100 last:border-0 px-1 w-full">
      <div className="w-full flex justify-start mb-2">
        <Tooltip title={tooltip}>
          <span className="font-bold text-sm text-gray-800 cursor-help flex items-center gap-1">
            {title}
          </span>
        </Tooltip>
      </div>
      <div className="w-full flex justify-start gap-4 font-semibold text-xs mb-2">
        <span style={{ color: r1.color }}>A: {def.format(v1)}</span>
      </div>
      <div className="w-full mt-2">
        <CoinsVisual r1={r1} r2={r2} v1={v1} v2={v2} def={def} />
      </div>
    </div>
  );
};

export const SmartMetricsCompareView = ({
  runs,
  overallWinnerId,
}: {
  runs: CompareRun[];
  overallWinnerId: string | null;
}) => {
  const [r1, r2] = runs;

  const getV = (r: CompareRun, key: string) => runMetric(r.run, key as any);
  const getDef = (key: string) => METRICS.find((m) => m.key === key);

  return (
    <div className="space-y-4">
      {/* Run headers */}
      <div className="grid grid-cols-2 gap-4">
        {runs.map((r, idx) => (
          <div
            key={r.id}
            className="rounded-xl px-5 py-4 flex items-center gap-3 shadow-sm border-2"
            style={{
              backgroundColor: `${r.color}0A`,
              borderColor: `${r.color}30`,
            }}
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm text-lg"
              style={{ backgroundColor: r.color }}
            >
              <ApiOutlined />
            </div>
            <span
              className="font-bold text-base tracking-wider uppercase truncate"
              style={{ color: "#1f2937" }}
            >
              MODEL {idx === 0 ? "A" : "B"}: {r.label}
            </span>
            {overallWinnerId === r.id && (
              <TrophyFilled className="text-amber-500 text-2xl ml-auto drop-shadow-sm" />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* QUALITY & ACCURACY */}
        <div className="rounded-3xl bg-[#fdfcff] border-2 border-indigo-50 shadow-sm p-6 flex flex-col relative overflow-hidden">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-indigo-50">
            <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800">
              Quality & Accuracy
            </h3>
            <div className="flex gap-2 text-2xl text-indigo-400">
              <SearchOutlined />
              <SafetyCertificateOutlined />
            </div>
          </div>

          <MetricRow
            title="Accuracy"
            tooltip="The percentage of correct responses, where higher indicates outputs closely matched expected truth."
            r1={r1}
            r2={r2}
            v1={getV(r1, "accuracyRate")}
            v2={getV(r2, "accuracyRate")}
            def={getDef("accuracyRate")}
          >
            <GaugeVisual
              r1={r1}
              r2={r2}
              v1={getV(r1, "accuracyRate")}
              v2={getV(r2, "accuracyRate")}
              def={getDef("accuracyRate")}
            />
          </MetricRow>

          <MetricRow
            title="Avg Judge"
            tooltip="The average score (out of 10) awarded by the LLM-as-a-judge for subjective response quality."
            r1={r1}
            r2={r2}
            v1={getV(r1, "averageQualityScore")}
            v2={getV(r2, "averageQualityScore")}
            def={getDef("averageQualityScore")}
          >
            <StarsVisual
              r1={r1}
              r2={r2}
              v1={getV(r1, "averageQualityScore")}
              v2={getV(r2, "averageQualityScore")}
              def={getDef("averageQualityScore")}
            />
          </MetricRow>

          <MetricRow
            title="Success Rate"
            tooltip="The percentage of requests that completed successfully without errors or timeouts."
            r1={r1}
            r2={r2}
            v1={getV(r1, "successRate")}
            v2={getV(r2, "successRate")}
            def={getDef("successRate")}
          >
            <CircleVisual
              r1={r1}
              r2={r2}
              v1={getV(r1, "successRate")}
              v2={getV(r2, "successRate")}
              def={getDef("successRate")}
            />
          </MetricRow>
        </div>

        {/* PERFORMANCE */}
        <div className="rounded-3xl bg-[#fcfdff] border-2 border-sky-50 shadow-sm p-6 flex flex-col relative overflow-hidden">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-sky-50">
            <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800">
              Performance
            </h3>
            <div className="flex gap-2 text-2xl text-sky-400">
              <FieldTimeOutlined />
              <DashboardOutlined />
            </div>
          </div>

          <div className="flex flex-col gap-6 flex-1">
            <MetricRow
              title="Avg Latency"
              tooltip="The average time taken for the model to generate the full response."
              r1={r1}
              r2={r2}
              v1={getV(r1, "averageLatencyMs")}
              v2={getV(r2, "averageLatencyMs")}
              def={getDef("averageLatencyMs")}
            >
              <BarVisual
                r1={r1}
                r2={r2}
                v1={getV(r1, "averageLatencyMs")}
                v2={getV(r2, "averageLatencyMs")}
                def={getDef("averageLatencyMs")}
              />
            </MetricRow>

            <MetricRow
              title="P95 Latency"
              tooltip="The 95th percentile latency; 95% of requests completed faster than this time."
              r1={r1}
              r2={r2}
              v1={getV(r1, "p95LatencyMs")}
              v2={getV(r2, "p95LatencyMs")}
              def={getDef("p95LatencyMs")}
            >
              <BarVisual
                r1={r1}
                r2={r2}
                v1={getV(r1, "p95LatencyMs")}
                v2={getV(r2, "p95LatencyMs")}
                def={getDef("p95LatencyMs")}
              />
            </MetricRow>
          </div>
        </div>

        {/* COST & EFFICIENCY */}
        <div className="rounded-3xl bg-[#fcfffd] border-2 border-emerald-50 shadow-sm p-6 flex flex-col relative overflow-hidden">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-emerald-50">
            <h3 className="font-bold text-sm uppercase tracking-widest text-slate-800">
              Cost & Efficiency
            </h3>
            <div className="flex gap-2 text-2xl text-emerald-500">
              <DollarCircleOutlined />
              <AccountBookOutlined />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 h-full items-start">
            <CostRow
              title="Total Tokens"
              tooltip="The total number of prompt and completion tokens processed."
              r1={r1}
              r2={r2}
              v1={getV(r1, "totalTokens")}
              v2={getV(r2, "totalTokens")}
              def={getDef("totalTokens")}
            />
            <CostRow
              title="Total Cost"
              tooltip="The estimated total API cost for running these cases."
              r1={r1}
              r2={r2}
              v1={getV(r1, "totalCost")}
              v2={getV(r2, "totalCost")}
              def={getDef("totalCost")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
